import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import {
  getLookupQueryKey,
  getLookupQueryOptions,
  prefetchLookup,
  useLookup,
} from '@unifyapps/network/generated/lookup-rest-api/lookup-rest-api';
import type { LookupRequest } from '@unifyapps/network/generated/models/lookupRequest';
import { Op } from '@unifyapps/network/generated/models/op';
import type { QueryClient } from '@unifyapps/network/react-query';
import { tryCatch } from '../../utils/await-to-js';

export interface PlatformAutomationConfig {
  type: string;
  automationId: string;
  synchronous?: boolean;
}

type PlatformAutomationConfigLookupResponse = {
  response?: { objects?: { properties: PlatformAutomationConfig }[] };
};

export enum PlatformAutomationConfigType {
  KnowledgeGetNextSyncTime = 'GET_NEXT_SCHEDULED_SYNC_TIME',
  GetTraceDetailsNew = 'GET_TRACE_DETAILS_NEW',
  GenerateFigmaPage = 'GENERATE_FIGMA_PAGE',
  WorkflowAgentResponseSchemaGeneration = 'WORKFLOW_AGENT_RESPONSE_SCHEMA_GENERATION',
  WorflowAISummary = 'WORKFLOW_AI_SUMMARY',
  RecentThemes = 'RECENT_THEMES',
  WorkflowNodeAISummary = 'WORKFLOW_AND_NODE_SUMMARY',
  TestCaseSummary = 'TEST_CASE_SUMMARY',
  ObjectRecordsExport = 'OBJECT_RECORDS_EXPORT',
  PreviewFileImportError = 'FILE_IMPORT_PREVIEW',
  ExportChangeset = 'EXPORT_CHANGESET',
  PlatformAIFilter = 'PLATFORM_AI_FILTER',
  ExportWorkflowRuns = 'WORKFLOW_RUNS_EXPORT',
  ExportWorkflowActivity = 'WORKFLOW_ACTIVITY_EXPORT',
  ExportObjectActivity = 'OBJECT_ACTIVITY_EXPORT',
  AIAgentTaskFetcher = 'AI_AGENT_TASK_FETCHER',
  AIAgentActionsFetcher = 'AI_AGENT_ACTIONS_FETCHER',
  AIAgentResponseTraces = 'AI_AGENT_RESPONSE_TRACES',
  BulkCancelWorkflowRuns = 'BULK_CANCEL_WORKFLOW_RUNS',
  ExportApplicationDatasource = 'EXPORT_APPLICATION_DATASOURCE',
  TranslateApplicationPageLabels = 'TRANSLATE_APPLICATION_PAGE_LABELS',
  ExportLocalizationLabels = 'LOWCODE_LOCALIZATION_EXPORT',
  ImportLocalizationLabels = 'LOWCODE_LOCALIZATION_IMPORT',
  ExportInterfaceLabels = 'EXPORT_INTERFACE_LABELS',
  AddKnowledgeToAgent = 'KNOWLEDGE_ASSIGN',
  AIAgentGetKnowledgeStatsV2 = 'AI_AGENT_GET_KNOWLEDGE_STATS_V2',
  AIAgentGetKnowledgeStats = 'AI_AGENT_GET_KNOWLEDGE_STATS',
  KnowledgeSyncMetrics = 'KNOWLEDGE_SYNC_METRICS',
  KnowledgeCompletedMetrics = 'KNOWLEDGE_COMPLETED_METRICS',
  // time-bucketed crawl counts for the source Overview "items crawled over time" chart
  KnowledgeCrawlGraphStats = 'KNOWLEDGE_CRAWL_GRAPH_STATS',
  GetAgentAccessibleKnowledgeApps = 'GET_AGENT_ACCESSIBLE_KNOWLEDGE_APPS',
  AIAgentSaveAgent = 'AI_AGENT_SAVE_AGENT',
  SaveExternalAgent = 'SAVE_EXTERNAL_AGENT',
  GenerateRecordsWithAI = 'GENERATE_RECORDS_WITH_AI',
  AIAgentLookup = 'AI_AGENT_LOOKUP',
  WorkflowGPTStepValidator = 'WORKFLOW_GPT_SINGLE_STEP_VALIDATOR',
  AIWorkersList = 'LIST_AI_AGENT_AND_TEAMS',
  ImportSkillZip = 'IMPORT_SKILL_ZIP',
  SaveSkillWithTools = 'SAVE_SKILL_WITH_TOOLS',
  SaveSkillsToAdvancedAgent = 'SAVE_SKILL_AND_SKILL_GROUP_TO_ADVANCED_AGENT',
  PromptGenerator = 'PROMPT_GENERATOR',
  TestEvalMetric = 'EVAL_METRICS_TEST_METRIC',
  DatasetGenerateFromSessions = 'EVAL_DATASET_GENERATE_FROM_SESSIONS',
  DatasetGenerateFromPrompt = 'EVAL_DATASET_GENERATE_FROM_PROMPT',
  DatasetGenerateFromOTEL = 'EVAL_DATASET_GENERATE_FROM_OTEL',
  DatasetGenerateFromKnowledgeSet = 'EVAL_DATASET_GENERATE_FROM_KNOWLEDGE_SET',
  DatasetGenerateFromCSV = 'EVAL_DATASET_GENERATE_FROM_CSV',
  EvalRunExperiment = 'EVAL_EXPERIMENT_RUN_EXPERIMENT',
  // Agent Governance · Online evaluation — attaches a metric to an agent as a live evaluator.
  EvalGovernanceCreateEvaluator = 'EVAL_GOVERNANCE_CREATE_EVALUATOR',
  EvalGovernanceEvaluationSummary = 'EVAL_GOVERNANCE_EVALUATION_SUMMARY',
  EvalGovernanceFetchEvaluators = 'EVAL_GOVERNANCE_FETCH_EVALUATORS',
  AgentAssociationDetails = 'AI_AGENT_GET_AGENT_INFO',
  ExportTableData = 'EXPORT_TABULAR_DATA',
  SaveChart = 'EDIT_CHART_FROM_UI',
  PublishAgent = 'AI_AGENT_DEPLOY_AGENT',
  UpsertAIAgentTopic = 'UPSERT_TOPIC_ADVANCED_AGENT',
  // App connectors — app-scoped topics (isAppConnector) owning the app's tools; the upsert
  // reconciles child e_action_ai_agent bags by (topicId, context.resourceName), the fetch
  // hydrates them back without entity ids.
  UpsertAppTypeTopic = 'UPSERT_APP_TYPE_TOPIC',
  FetchAppTypeTopic = 'FETCH_APP_TYPE_TOPIC',
  DeleteAgent = 'AI_AGENT_DELETE_AGENT',
  // Deep-clones an agent along with its child entities (knowledge, tools, skills) — the plain
  // entity clone API only copies the child types it was written against, so skills were dropped.
  CloneAgent = 'AI_AGENT_CLONE_AGENT',
  AIAgentDeleteKnowledgeRawData = 'AI_AGENT_DELETE_KNOWLEDGE_RAW_DATA',
  AIAgentArchiveKnowledgeRawData = 'AI_AGENT_ARCHIVE_KNOWLEDGE_RAW_DATA',
  AIAgentIndexPeople = 'AI_AGENT_INDEX_PEOPLE',
  TraceDetails = 'GET_TRACE_DETAILS',
  GetTraceTimeline = 'GET_TRACE_TIMELINE',
  GetGlobalTraceTimeline = 'GET_GLOBAL_TRACE_TIMELINE',
  TraceTimelineDetails = 'TRACE_TIMELINE_DETAILS',
  // Agent Governance (agent_profile) variants of the trace drill-down automations — the reused
  // ai-agent observability views select these via context when scoped to a governance agent.
  GetGovernanceTraceDetailsNew = 'GET_GOVERNANCE_TRACE_DETAILS_NEW',
  GetGovernanceTraceTimeline = 'GET_GOVERNANCE_TRACE_TIMELINE',
  GovernanceTraceTimelineDetails = 'GOVERNANCE_TRACE_TIMELINE_DETAILS',
  // Agent Governance · Discovery — creates an agent_profile_group connection and kicks off discovery.
  AgentProfileGroupCreation = 'AGENT_PROFILE_GROUP_CREATION',
  // Agent Governance · Discovery — re-runs agent discovery for one agent_profile_group ("Sync now").
  // TBD(BE): placeholder value — rename when BE registers the automation config.
  AgentProfileGroupSync = 'AGENT_PROFILE_GROUP_SYNC',
  // Agent Governance · Discovery — registers (enables) every discovered agent of a group ("Register all").
  // TBD(BE): placeholder value — rename when BE registers the automation config.
  AgentProfileGroupRegisterAll = 'AGENT_PROFILE_GROUP_REGISTER_ALL',
  // Agent Governance · Discovery — batched { agentCount, llmCount } per agent_profile_group for
  // the listing's Discovered column; parameters { agentProfileGroupIds }.
  GetDiscoveredAssetCount = 'GET_DISCOVERED_ASSET_COUNT',
  // Agent Governance — open incident count per policy FOR ONE AGENT (a policy's incidents differ
  // by agent), driving the agent detail Policies tab; parameters { assetId, policyIds }.
  GetAgentPolicyIncidents = 'GET_AGENT_POLICY_INCIDENTS',
  // Agent Governance · Policies — after a policy's asset rules (ruleFilter) are edited, recomputes
  // which assets the policy applies to (its accessibleTo) from the new rules. parameters { policyId }.
  GovernanceRecomputePolicyAccessibleTo = 'GOVERNANCE_RECOMPUTE_POLICY_ACCESSIBLE_TO',
  // Agent Governance — lists a platform connection's external agents/models as {label,value}
  // options for the register/add-model pickers; the connection rides in runtimeConnections.
  GovernanceAgentModelLookup = 'GOVERNANCE_AGENT_MODEL_LOOKUP',
  // Agent Governance — the single create surface for agent_profile rows: bulk (one per assetId)
  // from the pickers, or single manual when assetIds is omitted.
  GovernanceCreateAgentsModels = 'GOVERNANCE_CREATE_AGENTS_MODELS',
  // Agent Governance — mints an OTLP ingest access key for the setup snippets ("Generate key");
  // no inputs, output { key }.
  GovernanceOtlpAccessProfile = 'GOVERNANCE_OTLP_ACCESS_PROFILE',
  // Agent Governance — aggregate activity for one governed asset (trace/request count, cost,
  // error rate, p90 latency + per-day counts) feeding the details Overview activity card.
  GovernanceAssetActivityMetrics = 'GOVERNANCE_ASSET_ACTIVITY_METRICS',
  // Agent Governance · FinOps — everything the Outcomes & ROI details page shows for one
  // outcome over a window: its totals, its per-day counts and the sources crediting it;
  // parameters { outcomeId, startTime, endTime } as epoch ms.
  GovernanceGetOutcomes = 'AI_GOVERNANCE_GET_OUTCOMES',
  // Agent Governance · FinOps — the same figures the other way round: everything the agent
  // details Outcomes tab shows for ONE AGENT over a window — its totals, its per-day counts and
  // the outcomes it is credited with; parameters { agentProfileId, startTime, endTime } as epoch ms.
  GovernanceGetAgents = 'AI_GOVERNANCE_GET_AGENTS',
  // Agent Governance — batched per-asset listing stats (last activity, telemetry count,
  // incidents, policies, cost) for the registry telemetry columns; parameters { aiAgentIds }.
  // ⚠️ BE seeded this properties_type MIXED-CASE — not the usual UPPER_SNAKE.
  AgentRegistryListing = 'Agent_Registry_Listing',
  // AI Models library — batched 30-day spend per model, summed from traces, for the Cost column;
  // parameters { modelIds }, output { modelCostDetails: [{ modelId, totalCost }] }.
  ModelLibraryModelLevelCost = 'MODEL_LIBRARY_MODEL_LEVEL_COST',
  GetVoiceAgentSessionDetails = 'AI_AGENT_FETCH_VOICE_SESSION_DETAILS',
  AnnotateHITLMetricScore = 'ANNOTATE_HITL_METRIC_SCORE',
  ExperimentFetchMetricAverage = 'EXPERIMENT_FETCH_METRIC_AVERAGE',
  ExperimentFetchCostAverage = 'EXPERIMENT_FETCH_COST_AVERAGE',
  CreateStory = 'CREATE_STORY',
  FetchStoryOfUser = 'FETCH_STORY_OF_USER',
  FetchUsersForStory = 'FETCH_USERS_FOR_STORY',
  LikeStory = 'LIKE_STORY',
  TextToNoCodeGenerateApp = 'TEXT_TO_NO_CODE_GENERATE_APP',
  TextToWorkflowGenerateWorkflow = 'TEXT_TO_WORFKLOW_GENERATE_WORKFLOW',
  StoryReactionCount = 'STORY_REACTION_COUNT',
  StoryViewCount = 'STORY_VIEW_COUNT',
  ViewStory = 'VIEW_STORY',
  ExperimentExportExperimentRun = 'EXPERIMENT_EXPORT_EXPERIMENT_RUN',
  EvalStopExperimentRun = 'EVAL_STOP_EXPERIMENT_RUN',
  AIAgentHighlightChunks = 'AI_AGENT_HIGHLIGHT_CHUNKS',
  AIAgentGetPeopleDetails = 'AI_AGENT_GET_PEOPLE_DETAILS',
  ExportApplicationUsers = 'EXPORT_APPLICATION_USERS',
  ExportUsers = 'USERS_EXPORT',
  ExportTeams = 'TEAMS_EXPORT',
  ExportRoles = 'ROLES_EXPORT',
  GenerateScenarioUsingLLM = 'GENERATE_SCENARIO_USING_LLM',
  ReGenerateScenarioUsingLLM = 'REGENERATE_SCENARIO_USING_LLM',
  MarkdownToCSV = 'MARKDOWN_TO_CSV',
  CustomIconUpload = 'CUSTOM_ICON_UPLOAD',
  EnterpriseSearchRecentSearches = 'RECENTSEARCH_QUERY',
  EnterpriseSearchAutocomplete = 'AUTOCOMPLETE_QUERY',
  EvalExportDatasets = 'EVAL_EXPORT_DATASETS',
  EvalDeleteScenario = 'EVAL_DELETE_A_SCENARIO',
  ToggleSchedulerExperimentRun = 'SCHEDULE_UNSCHEDULE_EXPERIMENT_RUN',
  ImportFilesToEntity = 'IMPORT_FILES_TO_ENTITY',
  FetchWorkflowAgent = 'WORKFLOW_AGENT_GET_WORKFLOW',
  SaveWorkflowAgent = 'WORKFLOW_AGENT_SAVE_WORKFLOW',
  WorkflowAgentSummarizeNode = 'WORKFLOW_AGENT_SUMMARIZE_NODE',
  EnterpriseSearchFilterMetadata = 'ENTEPRISE_SEARCH_FILTER_METADATA',
  CopilotThreadRecentDocuments = 'COPILOT_THREAD_RECENT_DOCUMENTS',
  CopilotThreadTrendingDocuments = 'COPILOT_THREAD_TRENDING_DOCUMENTS',
  FigmaLowCodeTransformation = 'FIGMA_LOW_CODE_TRANSFORMATION',
  // Copilot Storybook's live Adaptive-Card-transform section (Storybook/component/widgets/
  // AdaptiveCardWidget) posts a raw Adaptive Card against this type; the Groovy automation
  // behind it returns the expanded no-code block. Environments that have not registered the
  // config surface as a "missing config" error, which the widget shows inline.
  AdaptiveCardTransform = 'AC_NC_TRANSFORMER',
  // Runtime copilot chat (distinct from AC_NC_TRANSFORMER, which only expands card JSON): an
  // Adaptive Card's Action.Submit triggers the SUBMIT_ADAPTIVE_CARD_DATA data source, which calls
  // this automation with the card's submit payload read from the ADAPTIVE_CARD_DATA page variable.
  AdaptiveCardOnSubmit = 'ADAPTIVE_CARD_ON_SUBMIT',
  StreamMessagePublish = 'STREAM_MESSAGE_PUBLISH',
  StandardEntitiesGetConnections = 'STANDARD_ENTITIES_GET_CONNECTIONS',
  EvalCalculateEvaluationScale = 'EVAL_CALCULATE_EVALUATION_SCALE',
  ConnectionAuthDetails = 'CONNECTION_AUTH_DETAILS',
  GetAssetsInUACodePR = 'GET_ASSETS_IN_UACODE_PR',
  FetchDiffInWorkflow = 'FETCH_DIFF_IN_WORKFLOW',
  GenerateWorkflowRunInputData = 'GENERATE_WORKFLOW_RUN_INPUT_DATA',
  /**
   * Data Quality
   */
  DQMTermsDetector = 'DQM_TERMS_DETECTOR',
  DQMEntityRecordCount = 'DQM_ENTITY_RECORD_COUNT',
  DQMImportFileToEntity = 'DQM_IMPORT_FILE_TO_ENTITY',
  DQMTermsDetectorCallableScheduler = 'DQM_TERMS_DETECTER_CALLABLE_SCHEDULER',
  DQMUpdateBusinessTerms = 'DQM_UPDATE_BUSINESS_TERM_FIELD_MAPPINGS',
  DQMDetectBusinessTerm = 'DQM_DETECT_BUSINESS_TERM',
  DQMMonitoringExperimentsStats = 'DQM_MONITORING_EXPERIMENTS_STATS',
  DQMStartExperiment = 'DQM_START_EXPERIMENT',
  DQMStopExperiment = 'DQM_STOP_EXPERIMENT',
  DQMCreatePipeline = 'DQM_CREATE_PIPELINE',
  DQMCheckRunEnabled = 'DQM_CHECK_RUN_ENABLED',
  DQMFetchProfilingReport = 'DQM_FETCH_PROFILING_REPORT',
  DQMDataQualityEvalScheduler = 'DQM_DATA_QUALITY_EVAL_SCHEDULER',
  DQMRunMultipleRules = 'DQM_RUN_MULTIPLE_RULES',
  DQMFetchSuggestedRules = 'DQM_FETCH_SUGGESTED_RULES',
  DQMStartEntityQualityScan = 'DQM_START_ENTITY_QUALITY_SCAN',
  DQMTestDataQuality = 'DQM_TEST_DATA_QUALITY',
  DQMDetectFileDatatype = 'DQM_DETECT_FILE_DATATYPE',
  AddDQObjectsViaCSV = 'ADD_DQ_OBJECTS_VIA_CSV',
  AddMultipleDQObjectsViaCSV = 'ADD_MULTIPLE_DQ_OBJECTS_VIA_CSV',
  DQMSaveConnectionObjects = 'DQM_SAVE_CONNECTION_OBJECTS',
  DataQualityAssetScan = 'DATA_QUALITY_ASSET_SCAN',
  DQMLastRecordStatusRefresher = 'DQM_LAST_RECORD_STATUS_REFRESHER',
  DQMSaveAssociatedRulesForAsset = 'DQM_SAVE_ASSOCIATED_RULES_FOR_ASSET',
  DQMSaveAssociatedRulesForAssetFromCopilot = 'DQM_SAVE_ASSOCIATED_RULES_FOR_ASSET_FROM_COPILOT',
  DQMAddRuleKnowledge = 'DQM_ADD_RULE_KNOWLEDGE',
  DQMStartCopilot = 'DQM_START_COPILOT',
  GenericCopilotCreateCase = 'GENERIC_COPILOT_CREATE_CASE',
  AIAgentIndexRawData = 'AI_AGENT_INDEX_RAW_DATA',
  ChooseMemorySetsForAgent = 'CHOOSE_MEMORY_SETS_FOR_AGENT',
  KnowledgeReportingResyncKnowledge = 'AI_AGENT_RESYNC_KNOWLEDGE',
  WorkflowAgentImproveWithAI = 'WORKFLOW_AGENT_IMPROVE_WITH_AI',
  AgentPrerequisiteActionsOutputSchema = 'PREREQUISITE_ACTIONS_OUTPUT_SCHEMA',
  CustomComponentFileChecks = 'CUSTOM_COMPONENT_FILE_CHECKS',

  NoCodePageScore = 'NO_CODE_PAGE_SCORE',
  WorkflowScore = 'WORKFLOW_BEST_PRACTICE_SCORE',
  InterfaceWalkthroughForPage = 'INTERFACE_WALKTHROUGH_FOR_PAGE',
  AddKnowledge = 'ADD_KNOWLEDGE_ENTERPRISE_SEARCH',
  FetchKnowledgeEnterpriseSearch = 'FETCH_KNOWLEDGE_ENTERPRISE_SEARCH',
  EnterpriseSearchTestControl = 'TEST_CONTROL',
  GetAppKnowledgeSource = 'GET_APP_KNOWLEDGE_SOURCE',
  AICopilotFetchCaseList = 'AI_COPILOT_FETCH_CASE_LIST',
  // Every read the code builder's chat surface makes, behind ONE automation split by a
  // `fetchType` parameter. SESSION_HISTORY wraps AI_COPILOT_FETCH_CASE_LIST and joins
  // each chat's git branch and run status back on as `runtimeContext` — the copilot's
  // own projection carries no per-chat engine state, so without the wrapper the branch
  // pill has nothing to render. QUEUE_AND_LOCK_STATUS answers who holds a branch and who
  // is queued behind them, reading two engine-minted entity types the browser cannot
  // fetch directly (the entity API enforces type-level RBAC and no role grants either).
  // One config entry rather than one per read, so an environment cannot end up with half
  // of them seeded. Seeded in uacode.
  CodeBuilderFetchSessionData = 'CODE_BUILDER_FETCH_SESSION_DATA',
  AICopilotTokenCount = 'AI_COPILOT_TOKEN_COUNT',
  SubmitClientToolResults = 'Submit_Client_Tools',
  GetAffectedApplications = 'GET_AFFECTED_APPLICATIONS',
  EvictCacheForAgent = 'EVICT_CACHE_FOR_AGENT',
  DeleteThemeFromApplications = 'DELETE_THEME_FROM_APPLICATIONS',
  LiveEvalScheduler = 'LIVE_EVAL_SCHEDULER',
  CopilotFeedbackSchema = 'FEEDBACK_CATEGORY',
  ESDeleteRuntimeConnection = 'DELETE_RUNTIME_CONNECTION',
  ESCreateRuntimeConnection = 'CREATE_RUNTIME_CONNECTION',
  LinkBatchPreview = 'LINK_BATCH_PREVIEW',
  ObservabilityAgentSpending = 'OBSERVABILITY_AGENT_SPENDING',
  CopilotLookup = 'COPILOT_LOOKUP',
  PlatformFeatureFlag = 'PLATFORM_FEATURE_FLAG',
  CopilotUpdateRecordById = 'COPILOT_UPDATE_RECORD_BY_ID',
  CopilotDeleteRecordById = 'COPILOT_DELETE_RECORD_BY_ID',
  ExportToDocs = 'EXPORT_TO_DOCS',
  // AI Models — Model details "Linked assets" tab: the agents/workflows consuming a model;
  // parameters { modelId }, response is one row per consuming asset.
  FetchModelLinkedAssets = 'FETCH_MODEL_LINKED_ASSETS',

  /**
   * Support Hub (Help & Requests)
   */
  SupportFetchRequests = 'SUPPORT_FETCH_REQUESTS',
  SupportCreateRequest = 'SUPPORT_CREATE_REQUEST',
  SupportRequestDetail = 'SUPPORT_REQUEST_DETAIL',
  SupportRequestMessages = 'SUPPORT_REQUEST_MESSAGES',
  SupportSendMessage = 'SUPPORT_SEND_MESSAGE',
  SupportUpdateRequest = 'SUPPORT_UPDATE_REQUEST',
  SupportRequestEvents = 'SUPPORT_REQUEST_EVENTS',
  SupportUsers = 'SUPPORT_USERS',
  SupportDeleteRequest = 'SUPPORT_DELETE_REQUEST',
}

export const getPlatformAutomationConfigLookup = (configType: PlatformAutomationConfigType) => {
  return {
    type: 'ByQuery',
    lookupType: 'ENTITY',
    options: { entity_type: UAEntityTypes.PlatformAutomation },
    filter: { op: Op.EQUAL, field: 'properties_type', values: [configType] },
  } as LookupRequest;
};

export const getMissingPlatformAutomationConfigError = (configType: PlatformAutomationConfigType) =>
  new Error(`Platform automation config not found: ${configType}`);

const getConfigFromLookupResponse = (data?: PlatformAutomationConfigLookupResponse) =>
  data?.response?.objects?.[0]?.properties;

export default function useFetchPlatformAutomationConfig(
  configType: PlatformAutomationConfigType,
  options?: Partial<Omit<ReturnType<typeof getLookupQueryOptions>, 'select'>>,
) {
  const { data, isLoading, error } = useLookup<Awaited<PlatformAutomationConfigLookupResponse>>(
    getPlatformAutomationConfigLookup(configType),
    { query: { staleTime: Infinity, ...options } },
  );
  const config = getConfigFromLookupResponse(data);
  const isConfigMissing = !isLoading && !error && Boolean(data) && !config;
  const missingConfigError = isConfigMissing
    ? getMissingPlatformAutomationConfigError(configType)
    : undefined;

  return { config, isLoading, isConfigMissing, error: error ?? missingConfigError };
}

export const fetchPlatformAutomationConfigLookup = async (
  queryClient: QueryClient,
  configType: PlatformAutomationConfigType,
) => {
  const lookupRequest = getPlatformAutomationConfigLookup(configType);

  const [error, data] = await tryCatch(
    queryClient.fetchQuery(
      getLookupQueryOptions(lookupRequest, {
        query: {
          staleTime: Infinity,
        },
      }),
    ) as Promise<PlatformAutomationConfigLookupResponse>,
  );
  const config = getConfigFromLookupResponse(data);
  const missingConfigError =
    !error && data && !config ? getMissingPlatformAutomationConfigError(configType) : undefined;

  return { config, error: error ?? missingConfigError };
};

export const prefetchPlatformAutomationConfig = (
  queryClient: QueryClient,
  configType: PlatformAutomationConfigType,
) => {
  return prefetchLookup(queryClient, getPlatformAutomationConfigLookup(configType));
};

export const fetchPlatformAutomationConfig = async (
  queryClient: QueryClient,
  configType: PlatformAutomationConfigType,
) => {
  const lookupRequest = getPlatformAutomationConfigLookup(configType);
  await prefetchLookup(queryClient, lookupRequest);
  return queryClient.getQueryData<{
    response?: { objects?: { properties: PlatformAutomationConfig }[] };
  }>(getLookupQueryKey(lookupRequest))?.response?.objects?.[0]?.properties;
};
